import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import styles from '../../assets/scss/QuantitySelect/QuantitySelect.module.scss';

type Props = {
  value: number;
  choices: number[];
  onChange: (value: number) => void;
  triggerClassName?: string;
  ariaLabel?: string;
};

let idCounter = 0;

export function QuantitySelect({ value, choices, onChange, triggerClassName, ariaLabel }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(() => Math.max(choices.indexOf(value), 0));
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const listboxIdRef = useRef<string>();
  if (!listboxIdRef.current) {
    listboxIdRef.current = `quantity-select-${idCounter++}`;
  }
  const listboxId = listboxIdRef.current;

  useEffect(() => {
    if (!isOpen) return;

    const onOutside = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, highlightedIndex]);

  const openPanel = () => {
    setHighlightedIndex(Math.max(choices.indexOf(value), 0));
    setIsOpen(true);
  };

  const closePanel = (focusTrigger: boolean) => {
    setIsOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  };

  const commitIndex = (index: number) => {
    const next = choices[index];
    if (next === undefined) return;
    onChange(next);
    closePanel(true);
  };

  const onTriggerClick = () => {
    if (isOpen) {
      closePanel(false);
    } else {
      openPanel();
    }
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (isOpen) {
          setHighlightedIndex((i) => Math.min(i + 1, choices.length - 1));
        } else {
          openPanel();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setHighlightedIndex((i) => Math.max(i - 1, 0));
        } else {
          openPanel();
        }
        break;
      case 'Home':
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(choices.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen) {
          commitIndex(highlightedIndex);
        } else {
          openPanel();
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closePanel(true);
        }
        break;
      case 'Tab':
        if (isOpen) setIsOpen(false);
        break;
    }
  };

  return (
    <div
      className={styles.root}
      ref={rootRef}
    >
      <button
        type="button"
        ref={triggerRef}
        className={`${styles.trigger} ${triggerClassName ?? ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-activedescendant={isOpen ? `${listboxId}-${highlightedIndex}` : undefined}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={styles.value}>{value}</span>
        <span className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`} />
      </button>

      {isOpen && (
        <ul
          className={styles.panel}
          role="listbox"
          id={listboxId}
        >
          {choices.map((choice, index) => (
            <li
              key={choice}
              id={`${listboxId}-${index}`}
              ref={(el) => (optionRefs.current[index] = el)}
              role="option"
              aria-selected={choice === value}
              className={`${styles.option} ${choice === value ? styles.selected : ''} ${
                index === highlightedIndex ? styles.highlighted : ''
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => commitIndex(index)}
            >
              {choice}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
